import { Injectable } from '@angular/core';
import { first } from 'rxjs/operators';
import { BusinessTransactionType } from '../../../foundation/models/enums/business-transaction-type.enum';
import { ISourceSystemType } from '../../../foundation/models/enums/source-system-type.enum';
import { TransactionService } from '../../../titleregistration/services/transaction.service';
import { NewBusinessTransaction } from '../../../transaction/models/new-business-transaction.model';
import { WIPStatusType } from '../../../wip/models/enums/w-i-p-status-type.enum';
import { BusinessTransactionMetaData } from '../../models/business-transaction/business-transaction-metadata.model';
import { BusinessTransactionService } from '../business-transaction/business-transaction.service';
import { MvdModalService } from '../modal/mvd-modal.service';
import { TransactionOverrideService } from './transaction-override.service';

@Injectable()
export class TitleRegistrationTransactionOverrideService extends TransactionOverrideService {

    private businessTransactionType: BusinessTransactionType = new BusinessTransactionType();
    private wipStatusType: WIPStatusType = new WIPStatusType();

    constructor(
        private transactionService: TransactionService,
        private modalService: MvdModalService,
        public _businessTransactionService: BusinessTransactionService) {
        super();
    }

    public get sourceSystemType(): ISourceSystemType {
        return this.sourceSystemTypes.TitleRegistration;
    }

    /**
     * Determines if the route should be overriden with a new business transaction.  If so, a new transaction
     * number will be passed back.  If the transaction should not be overridden, the original transaction number
     * will be returned.
     */
    public async getTransactionNumber(businessTransactionNumberId: number, metaData: BusinessTransactionMetaData): Promise<number> {
        if (metaData.wipStatusTypeId !== this.wipStatusType.Active.id && metaData.wipStatusTypeId !== this.wipStatusType.Inactive.id && metaData.wipStatusTypeId !== this.wipStatusType.OnHold.id) {
            return businessTransactionNumberId;
        }

        if (+metaData.businessTransactionTypeId === this.businessTransactionType.TitleOnly.id || +metaData.businessTransactionTypeId === this.businessTransactionType.TitleandRegistration.id || +metaData.businessTransactionTypeId === this.businessTransactionType.RegistrationOnly.id) {
            const answered: boolean = await this.transactionService.overrideQuestionAnswered(businessTransactionNumberId).pipe(first()).toPromise();
            if (answered) {
                return businessTransactionNumberId;
            }
        }

        // This is the key type for Vehicle.  Using the number since there is no enum setup for the item key types.
        const vehicleKey = metaData.itemKeyCollection.find(i => i.businessTransactionItemKeyTypeId === 2);
        const customerKey = metaData.itemKeyCollection.find(i => i.businessTransactionItemKeyTypeId === 1);
        if (+metaData.businessTransactionTypeId === this.businessTransactionType.TitleOnly.id) {
            if (vehicleKey) {
                const isAro: boolean = await this.transactionService.isRegistrationAROSPI(vehicleKey.itemKeyId).pipe(first()).toPromise();
                if (isAro) {
                    let response = await this.modalService.confirm().title('Alert').body('ARO or SPI record exists for this vehicle. Do you want to use the existing registration for issuing the title?')
                        .okBtn('Yes').cancelBtn('No')
                        .open().afterClosed().toPromise();

                    if (response) {
                        return this.handleAROResponse(metaData, vehicleKey, customerKey, businessTransactionNumberId, this.businessTransactionType.TitleOnly.id);
                    } else {
                        this.transactionService.setOverrideQuestionAnswerKey(businessTransactionNumberId).pipe(first()).toPromise();
                        return businessTransactionNumberId;
                    }
                } else {
                    return businessTransactionNumberId;
                }
            }
        } else if (+metaData.businessTransactionTypeId === this.businessTransactionType.TitleandRegistration.id) {
            if (vehicleKey) {
                const isAro: boolean = await this.transactionService.isRegistrationAROSPI(vehicleKey.itemKeyId).pipe(first()).toPromise();
                if (isAro) {
                    let response = await this.modalService.confirm().title('Alert').body('ARO or SPI record exists for this vehicle. Do you want to use the existing registration for issuing the title?')
                        .okBtn('Yes').cancelBtn('No')
                        .open().afterClosed().toPromise();

                    if (response) {
                        return this.handleAROResponse(metaData, vehicleKey, customerKey, businessTransactionNumberId, this.businessTransactionType.TitleandRegistration.id);
                    } else {
                        this.transactionService.setOverrideQuestionAnswerKey(businessTransactionNumberId).pipe(first()).toPromise();
                        return await this.checkTRP(vehicleKey.itemKeyId, customerKey.itemKeyId, metaData, businessTransactionNumberId);
                    }
                } else {
                    const isTitleOnly: boolean = await this.transactionService.checkIsTitleOnly(vehicleKey.itemKeyId).pipe(first()).toPromise();
                    if (isTitleOnly) {
                        let response = await this.modalService.confirm().title('Alert').body('Title Only exists for this vehicle.  Do you want to add a Registration?')
                            .okBtn('Yes').cancelBtn('No')
                            .open().afterClosed().toPromise();

                        if (response) {
                            return this.handleTitleExistsResponse(metaData, vehicleKey, customerKey, businessTransactionNumberId, this.businessTransactionType.TitleandRegistration.id);
                        } else {
                            this.transactionService.setOverrideQuestionAnswerKey(businessTransactionNumberId).pipe(first()).toPromise();
                            return await this.checkTRP(vehicleKey.itemKeyId, customerKey.itemKeyId, metaData, businessTransactionNumberId);
                        }
                    } else {
                        await this.transactionService.setOverrideQuestionAnswerKey(businessTransactionNumberId).pipe(first()).toPromise();
                        return await this.checkTRP(vehicleKey.itemKeyId, customerKey.itemKeyId, metaData, businessTransactionNumberId);
                    }
                }
            }
        } else if (+metaData.businessTransactionTypeId === this.businessTransactionType.RegistrationOnly.id) {
            if (vehicleKey) {
                const isTitleOnly: boolean = await this.transactionService.checkIsTitleOnly(vehicleKey.itemKeyId).pipe(first()).toPromise();
                if (isTitleOnly) {
                    let response = await this.modalService.confirm().title('Alert').body('Title Only exists for this vehicle.  Do you want to add a Registration?')
                        .okBtn('Yes').cancelBtn('No')
                        .open().afterClosed().toPromise();

                    if (response) {
                        return this.handleTitleExistsResponse(metaData, vehicleKey, customerKey, businessTransactionNumberId, this.businessTransactionType.RegistrationOnly.id);
                    } else {
                        return businessTransactionNumberId;
                    }
                } else {
                    return businessTransactionNumberId;
                }
            }
        } else {
            return businessTransactionNumberId;
        }
    }

    async createNewTitleRegistrationTransaction(metaData: BusinessTransactionMetaData, vehicleId: number, customerNumberId: number, addTitle: boolean, addRegistration: boolean, oldTransactionNumberId: number) {
        const creationResult = await this.transactionService.createTitleRegistrationWithOptions(customerNumberId, vehicleId, metaData.agentId, addTitle, addRegistration).pipe(first()).toPromise();

        const newTransactionNumberId = creationResult.result;
        const businessTransaction: NewBusinessTransaction = new NewBusinessTransaction();
        businessTransaction.agentId = metaData.agentId;
        businessTransaction.businessTransactionNumberId = newTransactionNumberId;
        businessTransaction.businessTransactionParamId = vehicleId;
        businessTransaction.businessTransactionTypeId = this.businessTransactionType.TitleandRegistration.id;
        businessTransaction.businessTransactionAreaId = 3;
        businessTransaction.customerNumberId = customerNumberId;
        const transactions: NewBusinessTransaction[] = new Array<NewBusinessTransaction>();
        transactions.push(businessTransaction);

        const transactionNumbers = new Array<number>();
        transactionNumbers.push(newTransactionNumberId);

        await this.transactionService.checkin(transactionNumbers, metaData.agentId, transactions).pipe(first()).toPromise();

        await this._businessTransactionService.assignBusinessTransactionsToUser(newTransactionNumberId, 0, true).pipe(first()).toPromise();

        this._businessTransactionService.expireBusinessTransaction(oldTransactionNumberId.toString()).pipe(first()).toPromise();

        return newTransactionNumberId;
    }

    async handleAROResponse(metaData, vehicleKey, customerKey, oldTransactionNumberId: number, transactionTypeId: number) {
        if (transactionTypeId === this.businessTransactionType.TitleandRegistration.id) {
            await this.transactionService.setActiveRegistrationForVehicle(vehicleKey.itemKeyId, oldTransactionNumberId).pipe(first()).toPromise();
            await this.transactionService.setOverrideQuestionAnswerKey(oldTransactionNumberId).pipe(first()).toPromise();
            return oldTransactionNumberId;
        } else {
            const newTransactionId = await this.createNewTitleRegistrationTransaction(metaData, vehicleKey.itemKeyId, customerKey.itemKeyId, false, true, oldTransactionNumberId);
            await this.transactionService.setOverrideQuestionAnswerKey(newTransactionId).pipe(first()).toPromise();
            return newTransactionId;
        }
    }

    async handleTitleExistsResponse(metaData, vehicleKey, customerKey, oldTransactionNumberId: number, transactionTypeId: number) {
        if (transactionTypeId === this.businessTransactionType.TitleandRegistration.id) {
            await this.transactionService.setActiveTitleForVehicle(vehicleKey.itemKeyId, oldTransactionNumberId).pipe(first()).toPromise();
            await this.transactionService.setOverrideQuestionAnswerKey(oldTransactionNumberId).pipe(first()).toPromise();
            return oldTransactionNumberId;
        } else {
            const newTransactionId = await this.createNewTitleRegistrationTransaction(metaData, vehicleKey.itemKeyId, customerKey.itemKeyId, true, false, oldTransactionNumberId);
            await this.transactionService.setOverrideQuestionAnswerKey(newTransactionId).pipe(first()).toPromise();
            return newTransactionId;
        }
    }

    async checkTRP(vehicleId: number, customerNumberId: number, metaData, oldTransactionNumberId: number) {
        const permit = await this.transactionService.getActiveTRP(vehicleId).pipe(first()).toPromise();
        if (permit.result) {
            if (permit.result.customerNumberId !== customerNumberId) {
                let response = await this.modalService.confirm().title('Alert').body('Vehicle has an active TRP that is associated to another customer.  Do you want to use this TRP?')
                    .okBtn('Yes').cancelBtn('No')
                    .open().afterClosed().toPromise();

                if (response) {
                    return await this.createNewTitleRegistrationTransaction(metaData, vehicleId, permit.result.customerNumberId, false, false, oldTransactionNumberId);
                } else {
                    return oldTransactionNumberId;
                }
            } else {
                return oldTransactionNumberId;
            }
        } else {
            return oldTransactionNumberId;
        }
    }
}
